import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  extractErrorMessage,
  useGetInvitedUsers,
  useGetUser,
  useInviteUser,
} from '@sib/api';
import { useUserSetupStore } from '@sib/auth';
import { createTagFieldValidationStringMessage } from '@sib/ui';
import { useExpiringState } from '@spendbrain/common/hooks';
import { Invitee } from '@spendbrain/types/user';
import { z } from 'zod';

const MESSAGE_TIMEOUT = 6000;
export const MESSAGE_VISIBILITY_TIMEOUT = MESSAGE_TIMEOUT - 1000;

export enum ShareDialogFormField {
  EmailAddresses = 'emailAddresses',
}

interface UseValidationSchemaProps {
  invitees: Invitee[];
}

export const useValidationSchema = ({ invitees }: UseValidationSchemaProps) => {
  const { data: identity } = useGetUser();
  const domain = identity?.email.split('@')[1] || '';
  const { t } = useTranslation();

  const alreadyIncludedEmails = invitees.map(invitee => invitee.email);

  // TODO: re-consider different approach for tag field
  return z.object({
    [ShareDialogFormField.EmailAddresses]: z.array(
      z
        .string()
        .refine(
          email => {
            const isValidEmail = z.string().email().safeParse(email).success;

            return isValidEmail;
          },
          email => ({
            message: createTagFieldValidationStringMessage({
              message: t('components.shareDialog.validation.invalidEmail'),
              variant: 'error',
              tag: email,
            }),
          })
        )
        .refine(
          email => {
            const emailDomain = email.split('@')[1];

            return emailDomain === domain;
          },
          email => ({
            message: createTagFieldValidationStringMessage({
              message: t('components.shareDialog.validation.invalidDomain'),
              variant: 'error',
              tag: email,
            }),
          })
        )
        .refine(
          email => !alreadyIncludedEmails.includes(email),
          email => ({
            message: createTagFieldValidationStringMessage({
              message: t('components.shareDialog.validation.alreadyInvited'),
              variant: 'warning',
              tag: email,
            }),
          })
        )
    ),
  });
};

type ShareDialogFormValues = z.infer<ReturnType<typeof useValidationSchema>>;

const defaultValues: ShareDialogFormValues = {
  [ShareDialogFormField.EmailAddresses]: [],
};

interface UseFormPropsProps {
  invitees: Invitee[];
}

export const useFormProps = ({ invitees }: UseFormPropsProps) => {
  const validationSchema = useValidationSchema({ invitees });

  return useForm<ShareDialogFormValues>({
    defaultValues,
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: zodResolver(validationSchema),
  });
};

interface UseOnSubmitProps {
  onSuccess?: (invitees: Invitee[]) => void;
}

export const useOnSubmit = ({ onSuccess }: UseOnSubmitProps) => {
  const { t } = useTranslation();
  const [errorMessage, setErrorMessage] =
    useExpiringState<string>(MESSAGE_TIMEOUT);
  const [successMessage, setSuccessMessage] =
    useExpiringState<string>(MESSAGE_TIMEOUT);

  const { mutateAsync: inviteUser, ...rest } = useInviteUser({
    onSuccess: ({ success: addedUsers, failed: usersFailedToAdd }) => {
      if (addedUsers.length > 0) {
        const failedEmails = usersFailedToAdd
          .map(user => user.email)
          .join(', ');

        setSuccessMessage(
          t(
            usersFailedToAdd.length === 0
              ? 'components.shareDialog.shareAccess.successMessage.description'
              : 'components.shareDialog.shareAccess.partialSuccessMessage.description',
            {
              count: addedUsers.length,
              emails: failedEmails,
            }
          )
        );

        return onSuccess?.(addedUsers);
      }

      const error = usersFailedToAdd[0]?.error;

      setErrorMessage(
        error ||
          t('components.shareDialog.shareAccess.errorMessage.description')
      );
    },
    onError: error => {
      setErrorMessage(
        extractErrorMessage(
          error,
          t('components.shareDialog.shareAccess.errorMessage.description')
        )
      );
    },
  });
  const organizationId = useUserSetupStore(state => state.organizationId);
  const onSubmit = useCallback(
    async (values: ShareDialogFormValues) => {
      try {
        await inviteUser({
          emails: values[ShareDialogFormField.EmailAddresses],
          organizationId,
        });
      } catch (e) {
        return e;
      }
    },
    [inviteUser, organizationId]
  );

  return { ...rest, errorMessage, successMessage, onSubmit };
};

export const useInviteesList = () => {
  const [invitees, setInvitees] = useState<Invitee[]>([]);
  const { isPending, isFetching, data } = useGetInvitedUsers();

  const onInviteesAdd = (invitees: Invitee[]) =>
    setInvitees(previousData => {
      const nonDuplicateInvitees = invitees.filter(
        invitee =>
          !previousData.some(prevInvitee => prevInvitee.email === invitee.email)
      );

      return [...nonDuplicateInvitees, ...previousData];
    });

  const onInviteeRemove = (invitee: Invitee) =>
    setInvitees(previousData =>
      previousData.filter(i => i.email !== invitee.email)
    );

  useEffect(() => {
    if (data) {
      setInvitees(data);
    }
  }, [data]);

  return {
    invitees,
    onInviteeRemove,
    onInviteesAdd,
    isLoading: isPending || isFetching,
  };
};
